import type { AxiosError } from 'axios'

const SENSITIVE_KEY_PATTERN = /(password|token|secret|authorization|api[_-]?key|credential)/i
const MAX_OBJECT_KEYS = 50
const MAX_STRING_LENGTH = 10_000

function tryParseJson(text: string): unknown | null {
    const trimmed = text.trim()

    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null
    }

    try {
        return JSON.parse(trimmed) as unknown
    } catch {
        return null
    }
}

function truncateString(value: string): string {
    if (value.length <= MAX_STRING_LENGTH) {
        return value
    }

    return `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
}

function isSensitiveKey(key: string): boolean {
    return SENSITIVE_KEY_PATTERN.test(key)
}

function redactSensitiveValue(key: string | null, value: unknown, depth = 0): unknown {
    if (key && isSensitiveKey(key)) {
        return '[REDACTED]'
    }

    if (value == null || typeof value === 'boolean' || typeof value === 'number') {
        return value
    }

    if (typeof value === 'string') {
        return truncateString(value)
    }

    if (typeof value === 'function') {
        return '[UNSERIALIZABLE]'
    }

    if (depth >= 5) {
        return '[REDACTED_DEEP_OBJECT]'
    }

    if (Array.isArray(value)) {
        return value.map((item) => redactSensitiveValue(null, item, depth + 1))
    }

    if (value instanceof FormData) {
        const entries: Record<string, unknown> = {}

        for (const [entryKey, entryValue] of value.entries()) {
            entries[entryKey] =
                entryValue instanceof File ? `[File: ${entryValue.name}]` : redactSensitiveValue(entryKey, entryValue, depth + 1)
        }

        return redactSensitiveValue(null, entries, depth + 1)
    }

    if (value instanceof URLSearchParams) {
        const entries: Record<string, unknown> = {}

        for (const [entryKey, entryValue] of value.entries()) {
            entries[entryKey] = redactSensitiveValue(entryKey, entryValue, depth + 1)
        }

        return entries
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>)

        if (entries.length > MAX_OBJECT_KEYS) {
            return '[REDACTED_LARGE_OBJECT]'
        }

        const safe: Record<string, unknown> = {}

        for (const [entryKey, entryValue] of entries) {
            safe[entryKey] = redactSensitiveValue(entryKey, entryValue, depth + 1)
        }

        return safe
    }

    return String(value)
}

function normalizeRequestData(data: unknown): unknown {
    if (data == null) {
        return null
    }

    if (typeof data === 'string') {
        return tryParseJson(data) ?? data
    }

    return data
}

export function getSentryAxiosRequestContext(error: AxiosError): Record<string, unknown> {
    const config = error.config

    if (!config) {
        return {}
    }

    return {
        method: config.method?.toUpperCase() ?? null,
        url: config.url ?? null,
        params: redactSensitiveValue(null, config.params ?? null),
        payload: redactSensitiveValue(null, normalizeRequestData(config.data))
    }
}
